/**
 * ハッシュナビゲーション（FR-LINK-01・既定 ON）。
 *
 * 共有しやすさを優先し、キーは既定で接頭辞なし（#page=12）。同一ページに 2 つ目以降の
 * ビューワが載ったときだけ、要素 id で名前空間化して衝突を避ける（#book2.page=12）。
 * 書き戻しは既定で履歴を汚さない（replaceState）。文脈が飛ぶ移動だけが `{push:true}` で
 * 履歴を 1 件積み、ブラウザの戻る/進むの着地点になる（種別の振り分けは呼び出し側の責務）。
 *
 * 語彙そのもの（page/z/x/y/lb・丸め・直列化）は hash-codec.ts が持つ。ここは
 * location の監視と書き戻し、そして接頭辞の確保だけを受け持つ。
 */
import type { DisposeRegistry } from '../core/dispose-registry';
import { applyHash, parseHash, sameHashState, type HashState } from './hash-codec';

/** 使用中のハッシュ接頭辞（同一 document 内の重複を防ぐ）。 */
const claimed = new Set<string>();

function claimPrefix(elementId: string): string {
  if (!claimed.has('')) {
    claimed.add('');
    return '';
  }
  const base = elementId ? `${elementId}.` : '.';
  let prefix = base;
  for (let n = 2; claimed.has(prefix); n += 1) prefix = `${base.slice(0, -1)}${n}.`;
  claimed.add(prefix);
  return prefix;
}

/** 書き戻しの履歴種別。 */
export interface WriteOptions {
  /** true のときだけ履歴を 1 件積む（既定は replaceState）。 */
  push?: boolean;
}

export class HashRouter {
  /** 確保した接頭辞（共有 URL の生成でも同じ接頭辞を使うため公開する）。 */
  readonly prefix: string;
  private applying = false;
  /** 最後に読み書きした状態（同じ状態の二重通知を落とすため）。 */
  private last: HashState | null = null;

  constructor(
    private readonly view: Window,
    private readonly registry: DisposeRegistry,
    elementId: string,
    private readonly onNavigate: (state: HashState) => void,
  ) {
    this.prefix = claimPrefix(elementId);
    // dispose で接頭辞を返し、再生成した同じ要素が既定の接頭辞を取り直せるようにする。
    this.registry.add('other', () => claimed.delete(this.prefix));
  }

  /** ハッシュの監視を始める。戻り値は URL で指定された初期状態（無ければ null）。 */
  start(): HashState | null {
    const handle = (): void => this.handleChange();
    this.registry.listen(this.view, 'hashchange', handle);
    // 戻る/進むは popstate で届く。ハッシュだけの移動では hashchange も続けて発火するため、
    // 同じ状態での二重通知は handleChange() 側で落とす。
    this.registry.listen(this.view, 'popstate', handle);
    this.last = this.read();
    return this.last;
  }

  /** 現在の状態を URL へ書き戻す。既定は履歴を増やさない。 */
  write(state: HashState, options: WriteOptions = {}): void {
    if (sameHashState(this.read(), state)) {
      this.last = state;
      return;
    }

    const hash = applyHash(this.view.location.hash, this.prefix, state);
    this.applying = true;
    const url = `${this.view.location.pathname}${this.view.location.search}${hash}`;
    const history = this.view.history;
    if (options.push === true) history.pushState(history.state, '', url);
    else history.replaceState(history.state, '', url);
    this.applying = false;
    this.last = state;
  }

  private handleChange(): void {
    if (this.applying) return;
    const state = this.read();
    if (state === null || sameHashState(this.last, state)) return;
    this.last = state;
    this.onNavigate(state);
  }

  private read(): HashState | null {
    return parseHash(this.view.location.hash, this.prefix);
  }
}
